import { pickedFile } from '../../utils/pickedFile.js'
import MediaCaptureField from '../common/MediaCaptureField.js'
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SelectField from '../common/SelectField.js';
import Alert from '../common/Alert.js';
import { DashboardLayout } from '../common/page_component/index.js';
import salaryService from '../../services/SalaryService.js';
import axiosInstance from '../../config/axios.js';
import { Wallet, Calendar, User as UserIcon, HandCoins, Plus } from "lucide-react";
import { validators, inputFilters, useValidation, ValidationMessage } from '../common/Validation.js';
import Button from '../common/Button.js';
import { useTranslation } from 'react-i18next';

const SALARY_TYPE_OPTIONS = [
  { value: 'fixed_monthly', label: 'Fixed Monthly Salary' },
  { value: 'daily_wage', label: 'Daily Wages' },
];

const getBasicSalaryLabel = (salaryType) =>
  salaryType === 'daily_wage' ? 'Basic Salary (earned from attendance)' : 'Basic Salary (Monthly)';

const getBasicSalaryPlaceholder = (salaryType) =>
  salaryType === 'daily_wage' ? 'Auto-filled from days worked × daily rate' : 'Auto-filled from monthly salary';

const getSalaryTypeLabel = (salaryType) =>
  salaryType === 'daily_wage' ? 'Daily Wages' : 'Fixed Monthly Salary';

const getRateLabel = (salaryType) =>
  salaryType === 'daily_wage' ? 'Daily Wage Rate' : 'Monthly Salary';

const SALARY_PAYMENT_MODES = ['upi', 'cash'];
const MONTH_VALUES = [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december',
];

const pastPayrollDefaults = () => {
  const past = new Date();
  past.setDate(1);
  past.setMonth(past.getMonth() - 1);
  return {
    month: MONTH_VALUES[past.getMonth()],
    year: String(past.getFullYear()),
  };
};

const formatCycleDate = (year, monthIndex, day) => {
  const d = new Date(year, monthIndex, day);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const getSalaryCyclePeriodLabel = (monthValue, year, cycleDay) => {
  const monthIndex = MONTH_VALUES.indexOf(String(monthValue || '').toLowerCase());
  const y = parseInt(year, 10);
  if (monthIndex < 0 || !y) return '';
  const day = Math.min(28, Math.max(1, Number(cycleDay) || 1));
  if (day <= 1) {
    const last = new Date(y, monthIndex + 1, 0).getDate();
    return `${formatCycleDate(y, monthIndex, 1)} – ${formatCycleDate(y, monthIndex, last)}`;
  }
  const prev = new Date(y, monthIndex - 1, 1);
  const end = new Date(y, monthIndex, day - 1);
  return `${formatCycleDate(prev.getFullYear(), prev.getMonth(), day)} – ${formatCycleDate(end.getFullYear(), end.getMonth(), end.getDate())}`;
};

const applyCalculationToSalaryForm = (data, setFormData) => {
  const calc = data?.salary_calculation;
  if (!calc) return;

  const salaryType = data.user?.salary_type || calc.salary_type || 'fixed_monthly';
  const basicSalary =
    salaryType === 'daily_wage'
      ? calc.actual_pay_for_days_worked ?? calc.suggested_basic_salary
      : calc.suggested_basic_salary ?? calc.monthly_salary;

  setFormData((prev) => ({
    ...prev,
    salary_type: salaryType,
    basic_salary:
      basicSalary != null && basicSalary !== ''
        ? Number(basicSalary).toFixed(2)
        : prev.basic_salary,
    deductions: salaryType === 'daily_wage' ? '0' : prev.deductions,
  }));
};

const AddSalary = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState(() => {
    const pastDefaults = pastPayrollDefaults();
    return {
    user_id: '',
    month: pastDefaults.month,
    year: pastDefaults.year,
    salary_type: 'fixed_monthly',
    basic_salary: '',
    allowances: '0',
    deductions: '0',
    bonus: '0',
    overtime_pay: '0',
    payment_mode: 'upi',
    transaction_id: '',
    remarks: '',
    status: 'credited'
    };
  });
  const [paymentScreenshot, setPaymentScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [users, setUsers] = useState([]);
  const [months, setMonths] = useState([]);
  const [salaryCycleDay, setSalaryCycleDay] = useState(1);
  const [loading, setLoading] = useState(false);
  const [netSalary, setNetSalary] = useState(0);
  const [attendanceData, setAttendanceData] = useState(null);
  const [loadingAttendance, setLoadingAttendance] = useState(false);
  const [showAttendanceDetails, setShowAttendanceDetails] = useState(false);
  const navigate = useNavigate();

  const years = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 5 + i);
  const statuses = ['pending', 'processing', 'credited', 'hold', 'cancelled'];
  const paymentModes = SALARY_PAYMENT_MODES;

  const { errors, validate, validateField } = useValidation();

  const validationRules = {
    user_id:       (v) => validators.select(v, 'user'),
    month:         (v) => validators.select(v, 'month'),
    year:          (v) => validators.required(v, 'Year'),
    basic_salary:  (v) => validators.required(v, 'Basic salary') || validators.number(v, { min: 0.01, label: 'Basic salary' }),
    allowances:    (v) => v ? validators.number(v, { min: 0, label: 'Allowances' }) : '',
    deductions:    (v) => v ? validators.number(v, { min: 0, label: 'Deductions' }) : '',
    bonus:         (v) => v ? validators.number(v, { min: 0, label: 'Bonus' }) : '',
    overtime_pay:  (v) => v ? validators.number(v, { min: 0, label: 'Overtime pay' }) : '',
  };

  const [loanData, setLoanData] = useState(null);
  const [applyAttendanceDeduction, setApplyAttendanceDeduction] = useState(false);
  const [applyLoanDeduction, setApplyLoanDeduction] = useState(false);

  useEffect(() => {
    fetchMonths();
  }, []);

  // Re-fetch users when month or year changes
  useEffect(() => {
    if (formData.month && formData.year) {
      fetchUsers();
    }
  }, [formData.month, formData.year]);

  useEffect(() => {
    calculateNetSalary();
  }, [formData.basic_salary, formData.allowances, formData.deductions, formData.bonus, formData.overtime_pay]);

  useEffect(() => {
    if (!attendanceData?.salary_calculation) return;

    let totalDeductions = 0;

    if (
      applyAttendanceDeduction &&
      attendanceData.salary_calculation.salary_type !== 'daily_wage'
    ) {
      totalDeductions += attendanceData.salary_calculation.attendance_deduction || 0;
    }

    if (applyLoanDeduction && loanData?.total_monthly_installment > 0) {
      totalDeductions += loanData.total_monthly_installment;
    }

    setFormData((prev) => ({
      ...prev,
      deductions: Math.round(totalDeductions).toString(),
    }));
  }, [applyAttendanceDeduction, applyLoanDeduction, attendanceData, loanData]);

  const fetchUsers = async () => {
    try {
      // Fetch available users based on selected month and year
      const response = await salaryService.getAvailableUsers(
        formData.month || null,
        formData.year || null
      );
      setUsers(response.data || []);
    } catch (err) {
      Alert.error('Failed to load users');
      console.error(err);
    }
  };

  const fetchMonths = async () => {
    try {
      const response = await salaryService.getAvailableMonths();
      setMonths(response.data || []);
      const cycle = response.salary_cycle;
      if (cycle) {
        setSalaryCycleDay(cycle.start_day || cycle.cycle_start_day || 1);
        setFormData((prev) => ({
          ...prev,
          month: cycle.default_month || prev.month,
          year: cycle.default_year ? String(cycle.default_year) : prev.year,
        }));
      }
    } catch (err) {
      console.error('Failed to load months', err);
    }
  };

  const calculateNetSalary = () => {
    const basic = parseFloat(formData.basic_salary) || 0;
    const allowances = parseFloat(formData.allowances) || 0;
    const deductions = parseFloat(formData.deductions) || 0;
    const bonus = parseFloat(formData.bonus) || 0;
    const overtime = parseFloat(formData.overtime_pay) || 0;
    
    const net = basic + allowances + bonus + overtime - deductions;
    setNetSalary(Math.round(net));
  };

  const getSelectedUser = () => users.find((u) => u.user_id === formData.user_id);

  const selectedUser = getSelectedUser();
  const userHasSalaryRate = selectedUser ? Number(selectedUser.monthly_salary) > 0 : false;

  const handleUserChange = (e) => {
    const userId = e.target.value;
    validateField('user_id', userId, validationRules.user_id);

    const updates = { user_id: userId };
    if (userId) {
      const user = users.find((u) => u.user_id === userId);
      if (user) {
        updates.salary_type = user.salary_type || 'fixed_monthly';
        const rate = Number(user.monthly_salary) || 0;
        if (updates.salary_type === 'fixed_monthly' && rate > 0) {
          updates.basic_salary = rate.toString();
        } else {
          updates.basic_salary = '';
        }
      }
    } else {
      updates.salary_type = 'fixed_monthly';
      updates.basic_salary = '';
    }

    setFormData((prev) => ({ ...prev, ...updates }));
    setAttendanceData(null);
    setLoanData(null);
    setApplyAttendanceDeduction(false);
    setApplyLoanDeduction(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    let filtered = value;
    const amountFields = ['basic_salary', 'allowances', 'deductions', 'bonus', 'overtime_pay'];
    if (amountFields.includes(name)) filtered = inputFilters.amount(value);

    setFormData((prev) => ({ ...prev, [name]: filtered }));

    if (validationRules[name]) {
      validateField(name, filtered, validationRules[name]);
    }
    
    if (name === 'month' || name === 'year') {
      setAttendanceData(null);
      setLoanData(null);
      setApplyAttendanceDeduction(false);
      setApplyLoanDeduction(false);
    }
  };

  const fetchAttendanceCalculation = async () => {
    if (!formData.user_id || !formData.month || !formData.year) {
      Alert.error('Please select User, Month, and Year first');
      return;
    }

    if (!userHasSalaryRate) {
      Alert.error(
        'This employee has no salary rate configured. Open User Management, edit the employee, set Salary Type and amount, then try again.'
      );
      return;
    }

    setLoadingAttendance(true);
    try {
      const response = await salaryService.calculateWithDeductions(
        formData.user_id,
        formData.month,
        formData.year
      );

      const calcPayload = response.data;
      setAttendanceData(calcPayload);
      setLoanData(calcPayload.loan_information);
      applyCalculationToSalaryForm(calcPayload, setFormData);
      setApplyAttendanceDeduction(false);

      Alert.success('Salary calculated with loan information!');
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to calculate salary');
      setAttendanceData(null);
      setLoanData(null);
    } finally {
      setLoadingAttendance(false);
    }
  };

  const handleScreenshotChange = (e) => {
    const file = pickedFile(e);
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        Alert.error('Payment screenshot must be less than 5MB');
        return;
      }
      setPaymentScreenshot(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setScreenshotPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeScreenshot = () => {
    setPaymentScreenshot(null);
    setScreenshotPreview(null);
    const fileInput = document.getElementById('screenshot-input');
    if (fileInput) fileInput.value = '';
  };

  const validateForm = () => {
    return validate(formData, validationRules);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);

    try {
      // Prepare submission data
      const submissionData = {
        ...formData,
        apply_attendance_deduction: applyAttendanceDeduction,
        apply_loan_deduction: applyLoanDeduction,
        loan_deduction_amount: applyLoanDeduction && loanData ? loanData.total_monthly_installment : 0
      };
      
      // Use the appropriate service method based on whether loan deduction is applied
      if (applyLoanDeduction && loanData && loanData.total_monthly_installment > 0) {
        await salaryService.createWithDeductions(submissionData, paymentScreenshot);
      } else {
        await salaryService.create(submissionData, paymentScreenshot);
      }
      
      Alert.success('Salary record created successfully!');
      setTimeout(() => navigate('/salaries'), 1500);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to create salary record');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return `₹ ${Math.round(amount || 0)}`;
  };

  return (
    <DashboardLayout>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-primary flex items-center gap-2">
            <Wallet className="w-6 h-6 sm:w-7 sm:h-7" />
            Add Salary Record
          </h1>
          <p className="text-xs sm:text-sm text-gray-600 mt-1">Create a new salary record for an employee</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm p-4 sm:p-6 lg:p-8 space-y-6 sm:space-y-8">
                  {/* Section 1: Basic Information */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Basic Information
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          User <span className="text-danger font-bold">*</span>
                        </label>
                        <SelectField variant="form"
                          name="user_id"
                          value={formData.user_id}
                          onChange={handleUserChange}
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.user_id ? 'border-red-400' : 'border-gray-300'}`}
                          required
                        >
                          <option value="">Select User</option>
                          {users.map((user) => (
                            <option key={user.user_id} value={user.user_id}>
                              {user.display_name}
                            </option>
                          ))}
                        </SelectField>
                        <ValidationMessage error={errors.user_id} />
                        {selectedUser && !userHasSalaryRate && (
                          <p className="text-xs text-red-600 mt-1">
                            No salary rate set for this employee. Update Salary Type and amount in User Management first.
                          </p>
                        )}
                        {selectedUser && userHasSalaryRate && (
                          <p className="text-xs text-gray-500 mt-1">
                            {getSalaryTypeLabel(selectedUser.salary_type)} — {getRateLabel(selectedUser.salary_type)}: ₹{Number(selectedUser.monthly_salary).toLocaleString('en-IN')}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Month <span className="text-danger font-bold">*</span>
                        </label>
                        <SelectField variant="form"
                          name="month"
                          value={formData.month}
                          onChange={handleChange}
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.month ? 'border-red-400' : 'border-gray-300'}`}
                          required
                        >
                          <option value="">Select Month</option>
                          {months.map((month) => (
                            <option key={month.value} value={month.value}>
                              {month.label}
                            </option>
                          ))}
                        </SelectField>
                        <ValidationMessage error={errors.month} />
                        {formData.month && formData.year && (
                          <p className="text-xs text-gray-500 mt-1">
                            Cycle: {attendanceData?.period?.period_label
                              || getSalaryCyclePeriodLabel(formData.month, formData.year, salaryCycleDay)}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Year <span className="text-danger font-bold">*</span>
                        </label>
                        <SelectField variant="form"
                          name="year"
                          value={formData.year}
                          onChange={handleChange}
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.year ? 'border-red-400' : 'border-gray-300'}`}
                          required
                        >
                          {years.map((year) => (
                            <option key={year} value={year}>
                              {year}
                            </option>
                          ))}
                        </SelectField>
                        <ValidationMessage error={errors.year} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Status
                        </label>
                        <SelectField variant="form"
                          name="status"
                          value={formData.status}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        >
                          {statuses.map((status) => (
                            <option key={status} value={status}>
                              {status.toUpperCase()}
                            </option>
                          ))}
                        </SelectField>
                      </div>

                      {/* Calculate from Attendance Button */}
                      <div className="sm:col-span-4">
                        <Button
                          onClick={fetchAttendanceCalculation}
                          disabled={loadingAttendance || !formData.user_id || !formData.month || !formData.year || !userHasSalaryRate}
                          icon={Calendar}
                          loading={loadingAttendance}
                        >
                          {loadingAttendance ? 'Calculating...' : 'Calculate Salary from Attendance'}
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Attendance Summary Section */}
                  {attendanceData && (
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm sm:text-base font-semibold text-blue-900 flex items-center gap-2">
                          <Calendar className="w-5 h-5" />
                          Attendance Report - {attendanceData.period.month_display} {attendanceData.period.year}
                          {attendanceData.period.period_label ? ` (${attendanceData.period.period_label})` : ''}
                        </h3>
                        <button
                          type="button"
                          onClick={() => setShowAttendanceDetails(!showAttendanceDetails)}
                          className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                        >
                          {showAttendanceDetails ? 'Hide Details' : 'Show Details'}
                        </button>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-3">
                        <div className="bg-white p-3 rounded border border-blue-100">
                          <p className="text-xs text-gray-600 mb-1">Total Days</p>
                          <p className="text-lg sm:text-xl font-bold text-gray-800">{attendanceData.period.total_days}</p>
                        </div>
                        <div className="bg-green-50 p-3 rounded border border-green-200">
                          <p className="text-xs text-gray-600 mb-1">Days Worked</p>
                          <p className="text-lg sm:text-xl font-bold text-green-600">{attendanceData.attendance_summary.days_worked}</p>
                        </div>
                        <div className="bg-red-50 p-3 rounded border border-red-200">
                          <p className="text-xs text-gray-600 mb-1">Days Absent</p>
                          <p className="text-lg sm:text-xl font-bold text-red-600">{attendanceData.attendance_summary.days_absent}</p>
                        </div>
                        <div className="bg-purple-50 p-3 rounded border border-purple-200">
                          <p className="text-xs text-gray-600 mb-1">Work Hours</p>
                          <p className="text-sm sm:text-base font-bold text-purple-600">{attendanceData.attendance_summary.total_work_formatted}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3 bg-white p-3 rounded border border-blue-100">
                        <div>
                          <p className="text-xs text-gray-600">
                            {attendanceData.salary_calculation.salary_type === 'daily_wage' ? 'Daily Wage Rate' : 'Monthly Salary'}
                          </p>
                          <p className="text-sm font-semibold text-gray-800">{formatCurrency(attendanceData.salary_calculation.monthly_salary)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600">
                            {attendanceData.salary_calculation.salary_type === 'daily_wage' ? 'Daily Rate' : 'Per Day Rate'}
                          </p>
                          <p className="text-sm font-semibold text-gray-800">{formatCurrency(attendanceData.salary_calculation.per_day_salary)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600">Calculated Pay</p>
                          <p className="text-sm font-semibold text-green-600">{formatCurrency(attendanceData.salary_calculation.actual_pay_for_days_worked)}</p>
                        </div>
                      </div>

                      {attendanceData.salary_calculation.salary_type !== 'daily_wage' && (
                      <div className="bg-red-50 p-3 rounded border border-red-200 mt-3">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <p className="text-xs text-gray-600 mb-1">Deduction for Absent Days</p>
                            <p className="text-lg font-bold text-red-600">{formatCurrency(attendanceData.salary_calculation.attendance_deduction)}</p>
                          </div>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={applyAttendanceDeduction}
                              onChange={(e) => setApplyAttendanceDeduction(e.target.checked)}
                              className="w-4 h-4 text-red-500 border-gray-300 rounded focus:ring-red-500"
                            />
                            <span className="text-xs font-medium text-gray-700">Apply Attendance Deduction</span>
                          </label>
                        </div>
                        <p className="text-xs text-gray-600 italic">
                          Note: Check the box to apply attendance deduction of {formatCurrency(attendanceData.salary_calculation.attendance_deduction)} to the Deductions field.
                        </p>
                      </div>
                      )}

                      {attendanceData.salary_calculation.salary_type === 'daily_wage' && (
                        <p className="text-xs text-green-700 mt-3 italic bg-green-50 p-3 rounded border border-green-200">
                          Daily wage employee: pay is calculated as daily rate × days worked. No absent-day deduction applies.
                        </p>
                      )}

                      {/* Attendance Details Dropdown */}
                      {showAttendanceDetails && attendanceData.attendance_details.length > 0 && (
                        <div className="mt-3 max-h-48 overflow-y-auto bg-white rounded border border-blue-100">
                          <div className="hidden md:block">
                            <table className="w-full text-xs">
                              <thead className="bg-blue-100 sticky top-0">
                                <tr>
                                  <th className="px-2 py-2 text-left">Date</th>
                                  <th className="px-2 py-2 text-left">{t('auth.login')}</th>
                                  <th className="px-2 py-2 text-left">Logout</th>
                                  <th className="px-2 py-2 text-left">Hours</th>
                                </tr>
                              </thead>
                              <tbody>
                                {attendanceData.attendance_details.map((att, idx) => (
                                  <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                                    <td className="px-2 py-2">{att.date}</td>
                                    <td className="px-2 py-2">{att.login_time || '-'}</td>
                                    <td className="px-2 py-2">{att.logout_time || '-'}</td>
                                    <td className="px-2 py-2">{att.work_hours}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          <div className="md:hidden p-2 space-y-2">
                            {attendanceData.attendance_details.map((att, idx) => (
                              <div key={idx} className="border border-gray-100 rounded-lg p-3 bg-gray-50">
                                <p className="font-semibold text-gray-900 text-xs mb-2">{att.date}</p>
                                <div className="grid grid-cols-2 gap-2 text-xs">
                                  <div>
                                    <span className="text-gray-500">{t('auth.login')}:</span>
                                    <p className="font-medium text-gray-900">{att.login_time || '-'}</p>
                                  </div>
                                  <div>
                                    <span className="text-gray-500">Logout:</span>
                                    <p className="font-medium text-gray-900">{att.logout_time || '-'}</p>
                                  </div>
                                  <div>
                                    <span className="text-gray-500">Hours:</span>
                                    <p className="font-medium text-gray-900">{att.work_hours}</p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Loan Information Section */}
                  {loanData && loanData.has_active_loans && (
                    <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mt-4">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm sm:text-base font-semibold text-orange-900 flex items-center gap-2">
                          <HandCoins className="w-5 h-5" />
                          Active Loan Information
                        </h3>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 sm:gap-4 mb-4">
                        <div className="bg-white p-3 rounded border border-orange-100">
                          <p className="text-xs text-gray-600 mb-1">Total Active Loans</p>
                          <p className="text-lg sm:text-xl font-bold text-orange-600">{loanData.total_loans}</p>
                        </div>
                        <div className="bg-white p-3 rounded border border-orange-100">
                          <p className="text-xs text-gray-600 mb-1">Monthly Installment</p>
                          <p className="text-lg sm:text-xl font-bold text-orange-600">{formatCurrency(loanData.total_monthly_installment)}</p>
                        </div>
                        <div className="bg-white p-3 rounded border border-orange-100">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={applyLoanDeduction}
                              onChange={(e) => setApplyLoanDeduction(e.target.checked)}
                              className="w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-blue-400"
                            />
                            <span className="text-xs font-medium text-gray-700">Apply Loan Deduction</span>
                          </label>
                          <p className="text-xs text-gray-500 mt-1">Deduct from salary</p>
                        </div>
                      </div>

                      {/* Loan Details Table */}
                      <div className="bg-white rounded border border-orange-100 overflow-hidden">
                        <div className="hidden md:block overflow-x-auto max-h-48">
                          <table className="w-full text-xs">
                            <thead className="bg-orange-100 sticky top-0">
                              <tr>
                                <th className="px-2 py-2 text-left">Loan ID</th>
                                <th className="px-2 py-2 text-left">Amount</th>
                                <th className="px-2 py-2 text-left">Paid</th>
                                <th className="px-2 py-2 text-left">Pending</th>
                                <th className="px-2 py-2 text-left">Frequency</th>
                                <th className="px-2 py-2 text-left">Monthly Deduction</th>
                              </tr>
                            </thead>
                            <tbody>
                              {loanData.loans.map((loan, idx) => (
                                <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                                  <td className="px-2 py-2 font-medium">{loan.loan_id}</td>
                                  <td className="px-2 py-2">{formatCurrency(loan.loan_amount)}</td>
                                  <td className="px-2 py-2 text-green-600">{formatCurrency(loan.amount_paid)}</td>
                                  <td className="px-2 py-2 text-red-600">{formatCurrency(loan.amount_pending)}</td>
                                  <td className="px-2 py-2 capitalize">{loan.repayment_frequency.replace(/_/g, ' ')}</td>
                                  <td className="px-2 py-2 font-semibold text-orange-600">{formatCurrency(loan.monthly_installment)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <div className="md:hidden max-h-48 overflow-y-auto p-2 space-y-2">
                          {loanData.loans.map((loan, idx) => (
                            <div key={idx} className="border border-orange-100 rounded-lg p-3 bg-orange-50/50">
                              <p className="font-semibold text-gray-900 text-xs mb-2">{loan.loan_id}</p>
                              <div className="grid grid-cols-2 gap-2 text-xs">
                                <div>
                                  <span className="text-gray-500">Amount:</span>
                                  <p className="font-medium text-gray-900">{formatCurrency(loan.loan_amount)}</p>
                                </div>
                                <div>
                                  <span className="text-gray-500">Paid:</span>
                                  <p className="font-medium text-green-600">{formatCurrency(loan.amount_paid)}</p>
                                </div>
                                <div>
                                  <span className="text-gray-500">Pending:</span>
                                  <p className="font-medium text-red-600">{formatCurrency(loan.amount_pending)}</p>
                                </div>
                                <div>
                                  <span className="text-gray-500">Monthly:</span>
                                  <p className="font-medium text-orange-600">{formatCurrency(loan.monthly_installment)}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      <p className="text-xs text-orange-700 mt-3 italic">
                        Note: If you check "Apply Loan Deduction", ₹ {loanData.total_monthly_installment.toFixed(2)} will be automatically deducted and loan repayment records will be created.
                      </p>
                    </div>
                  )}

                  {/* Section 2: Salary Components */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Salary Components
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Salary Type
                        </label>
                        <SelectField variant="form"
                          name="salary_type"
                          value={formData.salary_type}
                          onChange={handleChange}
                          disabled={!!formData.user_id}
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${
                            formData.user_id ? 'border-gray-200 bg-gray-50 text-gray-700 cursor-not-allowed' : 'border-gray-300'
                          }`}
                        >
                          {SALARY_TYPE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </SelectField>
                        {formData.user_id && (
                          <p className="text-xs text-gray-500 mt-1">
                            Loaded from employee profile{attendanceData ? ' · confirmed after attendance calculation' : ''}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          {getBasicSalaryLabel(formData.salary_type)} <span className="text-danger font-bold">*</span>
                        </label>
                        <input
                          type="text"
                          name="basic_salary"
                          value={formData.basic_salary}
                          onChange={handleChange}
                          placeholder={getBasicSalaryPlaceholder(formData.salary_type)}
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${
                            attendanceData ? 'bg-orange-50 border-orange-200' : ''
                          } ${errors.basic_salary ? 'border-red-400' : attendanceData ? '' : 'border-gray-300'}`}
                          required
                        />
                        {attendanceData && (
                          <p className="text-xs text-gray-500 mt-1">
                            {formData.salary_type === 'daily_wage'
                              ? `Calculated: ${formatCurrency(attendanceData.salary_calculation.actual_pay_for_days_worked)} (${attendanceData.attendance_summary.days_worked} days × ${formatCurrency(attendanceData.salary_calculation.per_day_salary)})`
                              : `Monthly rate: ${formatCurrency(attendanceData.salary_calculation.monthly_salary)} · Check attendance deduction above if needed`}
                          </p>
                        )}
                        <ValidationMessage error={errors.basic_salary} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Allowances
                        </label>
                        <input
                          type="text"
                          name="allowances"
                          value={formData.allowances}
                          onChange={handleChange}
                          placeholder="Enter allowances"
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.allowances ? 'border-red-400' : 'border-gray-300'}`}
                        />
                        <ValidationMessage error={errors.allowances} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Deductions
                        </label>
                        <input
                          type="text"
                          name="deductions"
                          value={formData.deductions}
                          onChange={handleChange}
                          placeholder="Enter deductions"
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.deductions ? 'border-red-400' : 'border-gray-300'}`}
                        />
                        <ValidationMessage error={errors.deductions} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Bonus
                        </label>
                        <input
                          type="text"
                          name="bonus"
                          value={formData.bonus}
                          onChange={handleChange}
                          placeholder="Enter bonus"
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.bonus ? 'border-red-400' : 'border-gray-300'}`}
                        />
                        <ValidationMessage error={errors.bonus} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Overtime Pay
                        </label>
                        <input
                          type="text"
                          name="overtime_pay"
                          value={formData.overtime_pay}
                          onChange={handleChange}
                          placeholder="Enter overtime pay"
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.overtime_pay ? 'border-red-400' : 'border-gray-300'}`}
                        />
                        <ValidationMessage error={errors.overtime_pay} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Net Salary (Calculated)
                        </label>
                        <div className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl bg-gray-50 text-sm sm:text-base font-semibold text-green-600">
                          {formatCurrency(netSalary)}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Section 3: Payment Details */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Payment Details (Optional)
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Payment Mode
                        </label>
                        <SelectField variant="form"
                          name="payment_mode"
                          value={formData.payment_mode}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        >
                          {paymentModes.map((mode) => (
                            <option key={mode} value={mode}>
                              {mode.replace(/_/g, ' ').toUpperCase()}
                            </option>
                          ))}
                        </SelectField>
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Transaction ID
                        </label>
                        <input
                          type="text"
                          name="transaction_id"
                          value={formData.transaction_id}
                          onChange={handleChange}
                          placeholder="Enter transaction ID"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        />
                      </div>

                      <div className="sm:col-span-2">
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Payment Screenshot
                        </label>
                        <MediaCaptureField mode="document" onPick={handleScreenshotChange} />
                        <p className="text-xs text-gray-600 mt-1">Upload payment screenshot (Max 5MB)</p>
                        {screenshotPreview && (
                          <div className="mt-3">
                            <div className="relative inline-block">
                              <img 
                                src={screenshotPreview} 
                                alt="Screenshot Preview" 
                                className="w-32 h-32 sm:w-40 sm:h-40 object-cover border border-gray-300 rounded-md"
                              />
                              <button
                                type="button"
                                onClick={removeScreenshot}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors text-sm"
                                title="Remove screenshot"
                              >
                                ×
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Section 4: Remarks */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Remarks
                    </h3>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                        Additional Notes
                      </label>
                      <textarea
                        name="remarks"
                        value={formData.remarks}
                        onChange={handleChange}
                        placeholder="Enter any additional notes or remarks"
                        rows="4"
                        className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                      />
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4 sm:pt-6 border-t">
                    <Button
                      type="submit"
                      loading={loading}
                      icon={Plus}
                    >
                      {loading ? 'Creating...' : 'Add Salary'}
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => navigate('/salaries')}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
    </DashboardLayout>
  );
};

export default AddSalary;